"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Clock, Calendar, Star, BookOpen, Users, Brain, Award } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProgrammesPricingOverview() {
  const {
    ref: mainHeaderRef,
    isVisible: mainHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: programme1Ref,
    isVisible: programme1Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: programme2Ref,
    isVisible: programme2Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: programme3Ref,
    isVisible: programme3Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: programme4Ref,
    isVisible: programme4Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: pricingHeaderRef,
    isVisible: pricingHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const programmes = [{
    id: 2,
    name: "Academic Essentials (Tutoring only)",
    sessionLength: "60 minutes",
    sessionsPerMonth: 4,
    payPerSession: 30,
    monthly: 108,
    annual: 1199
  }, {
    id: 1,
    name: "Academic Core (Tutoring + Blended Coaching)",
    sessionLength: "90 minutes",
    sessionsPerMonth: 4,
    payPerSession: 45,
    monthly: 169,
    annual: 1899
  }, {
    id: 3,
    name: "Coaching 1-to-1",
    sessionLength: "45 minutes",
    sessionsPerMonth: 4,
    payPerSession: 65,
    monthly: 240,
    annual: 2799
  }, {
    id: 4,
    name: "Academic Tutoring 1-to-1",
    sessionLength: "60 minutes",
    sessionsPerMonth: 4,
    payPerSession: 60,
    monthly: 220,
    annual: 2499
  }];
  return <section id="programmespricingoverview" className="py-16 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={mainHeaderRef} className={`text-center mb-16 transition-all duration-700 ${mainHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl font-display font-bold text-primary mb-4">
            Our Programmes
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Discover our comprehensive range of tutoring and coaching programmes designed to help students excel academically while building confidence and essential life skills.
          </p>
        </div>

        {}
        <div ref={programme1Ref} className={`mb-16 transition-all duration-700 ${programme1Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <img src="https://educationfamilytutors.com/wp-content/uploads/2026/03/Girl-stdydying-scaled.png" alt="Academic Essentials Tutoring Programme" className="w-full h-auto rounded-lg shadow-medium" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="size-8 text-secondary" />
                <h3 className="text-3xl font-display font-bold text-primary">
                  Academic Essentials (Tutoring Only)
                </h3>
              </div>
              <p className="text-foreground mb-4 leading-relaxed">
                Our Academic Essentials programme is perfect for students who need focused, high-quality tutoring in Science, Maths, or English. These group sessions provide expert instruction in a collaborative learning environment where students can engage with peers and receive personalised attention.
              </p>
              <p className="text-foreground mb-6 leading-relaxed">
                Each 60-minute session is carefully structured to cover key curriculum topics, reinforce understanding through practice, and build exam confidence. Students work through problems together, ask questions freely, and receive immediate feedback from qualified teachers.
              </p>
              <div className="bg-primary-light rounded-lg p-6 space-y-3">
                <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                  <Award className="size-5" />
                  Key Advantages:
                </h4>
                <ul className="space-y-2 text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Affordable group tutoring with expert teachers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Collaborative learning environment that builds peer support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Focused curriculum coverage for KS3 and GCSE levels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Flexible payment options to suit your budget</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Regular sessions to maintain consistent progress</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={programme2Ref} className={`mb-16 transition-all duration-700 ${programme2Visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <Users className="size-8 text-secondary" />
                  <Star className="size-4 text-secondary absolute -top-1 -right-1 fill-current" />
                </div>
                <h3 className="text-3xl font-display font-bold text-primary">
                  Academic Core (Tutoring + Blended Coaching)
                </h3>
              </div>
              <div className="inline-flex items-center gap-2 bg-secondary text-foreground-secondary px-4 py-2 rounded-full shadow-soft mb-4 font-bold text-sm">
                <Star className="size-4 fill-current" />
                Best Value - Most Popular
                <Star className="size-4 fill-current" />
              </div>
              <p className="text-foreground mb-4 leading-relaxed">
                Our flagship Academic Core programme combines the best of both worlds: expert academic tutoring with integrated coaching on mindset, focus, and study techniques. These extended 90-minute sessions provide comprehensive support that addresses not just what students learn, but how they learn.
              </p>
              <p className="text-foreground mb-6 leading-relaxed">
                Each session begins with 60 minutes of targeted academic instruction, followed by 30 minutes of coaching focused on building confidence, developing effective study habits, managing exam stress, and fostering a growth mindset. Students also receive our exclusive Exam Techniques e-book and access to a supportive WhatsApp group for ongoing help between sessions.
              </p>
              <div className="bg-secondary-light rounded-lg p-6 space-y-3 border-2 border-secondary">
                <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                  <Award className="size-5" />
                  Key Advantages:
                </h4>
                <ul className="space-y-2 text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Complete academic and personal development in one programme</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Extended 90-minute sessions for deeper learning and support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Builds exam confidence and effective study strategies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Free Exam Techniques e-book included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Exclusive WhatsApp group access for continuous support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground font-bold mt-1">•</span>
                    <span>Outstanding value combining tutoring and coaching</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503393/img/academic_core_blended_coaching.png" alt="Academic Core Tutoring and Coaching Programme" className="w-full h-auto rounded-lg shadow-medium" />
            </div>
          </div>
        </div>

        {}
        <div ref={programme3Ref} className={`mb-16 transition-all duration-700 ${programme3Visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503393/img/coaching_one_to_one_sessions.png" alt="One-to-One Coaching Programme" className="w-full h-auto rounded-lg shadow-medium" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="size-8 text-secondary" />
                <h3 className="text-3xl font-display font-bold text-primary">
                  Coaching 1-to-1
                </h3>
              </div>
              <p className="text-foreground mb-4 leading-relaxed">
                Our personalised Coaching 1-to-1 programme provides intensive, individualised support focused entirely on personal development, mindset, and study skills. These private 45-minute sessions are tailored to each student's unique needs, challenges, and goals.
              </p>
              <p className="text-foreground mb-6 leading-relaxed">
                Working one-on-one with an experienced coach, students develop essential skills in exam confidence, focus and concentration, motivation, time management, interview preparation, and building a positive growth mindset. This programme is ideal for students who need targeted support in overcoming specific challenges or developing particular skills beyond academic content.
              </p>
              <div className="bg-accent-light rounded-lg p-6 space-y-3">
                <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                  <Award className="size-5" />
                  Key Advantages:
                </h4>
                <ul className="space-y-2 text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Completely personalised coaching tailored to individual needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Private sessions for maximum focus and confidentiality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Develops critical life skills beyond academics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Free Exam Techniques e-book included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Addresses specific challenges like exam anxiety or motivation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-dark font-bold mt-1">•</span>
                    <span>Flexible focus areas chosen by student and parent</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={programme4Ref} className={`mb-20 transition-all duration-700 ${programme4Visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-1">
              <div className="flex items-center gap-3 mb-4">
                <Award className="size-8 text-secondary" />
                <h3 className="text-3xl font-display font-bold text-primary">
                  Academic Tutoring 1-to-1
                </h3>
              </div>
              <div className="inline-flex items-center gap-2 bg-accent text-foreground-accent px-4 py-2 rounded-full shadow-soft mb-4 font-bold text-sm">
                Limited Availability
              </div>
              <p className="text-foreground mb-4 leading-relaxed">
                Our premium Academic Tutoring 1-to-1 programme offers the ultimate in personalised academic support. These exclusive 60-minute private sessions provide completely individualised instruction in Science, Maths, or English, tailored precisely to each student's learning style, pace, and curriculum needs.
              </p>
              <p className="text-foreground mb-6 leading-relaxed">
                With the undivided attention of a qualified teacher, students receive bespoke lesson plans, immediate feedback, and targeted intervention on areas of difficulty. This programme is perfect for students who need intensive catch-up support, are working towards specific exam goals, or simply thrive with one-on-one instruction. Includes our comprehensive How to Revise e-book.
              </p>
              <div className="bg-primary-light rounded-lg p-6 space-y-3 border-2 border-primary">
                <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                  <Award className="size-5" />
                  Key Advantages:
                </h4>
                <ul className="space-y-2 text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Completely personalised academic instruction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>100% focus on individual learning needs and goals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Flexible curriculum coverage at student's own pace</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Free How to Revise e-book included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Ideal for exam preparation or intensive catch-up</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Limited spaces available for maximum quality</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503393/img/maths_tutoring_blackboard_corrected.png" alt="One-to-One Academic Tutoring Programme" className="w-full h-auto rounded-lg shadow-medium" />
            </div>
          </div>
        </div>

        {}
        <div ref={pricingHeaderRef} className={`text-center mb-12 pt-8 border-t-2 border-border transition-all duration-700 ${pricingHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl font-display font-bold text-primary mb-4">
            Programme Pricing Overview
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Choose the programme that best fits your needs. Our group tutoring options provide excellent value, 
            with limited availability for one-to-one sessions.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {programmes.map((programme, i) => <div key={programme.id} data-index={i} className="relative">
              {programme.id === 1 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 bg-secondary text-foreground-secondary px-6 py-2 rounded-full shadow-medium flex items-center gap-2 font-bold text-sm whitespace-nowrap">
                  <Star className="size-4 fill-current" />
                  Best Value - Recommended
                  <Star className="size-4 fill-current" />
                </div>}
              
              <Card className={`flex flex-col h-full transition-all duration-700 delay-${i * 100} ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${programme.id === 1 ? "border-4 border-secondary bg-secondary-light/10 shadow-strong hover:shadow-[0_12px_40px_rgba(0,0,0,0.20)] hover:scale-[1.02]" : "border-2 border-secondary hover:shadow-strong hover:-translate-y-1"}`}>
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="text-2xl text-primary font-display">
                    {programme.name}
                  </CardTitle>
                  
                </CardHeader>

                <CardContent className="flex-grow space-y-4">
                  <div className="flex items-center gap-3 pt-4">
                    <Clock className="size-5 text-secondary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Session Length</p>
                      <p className="font-semibold text-foreground">{programme.sessionLength}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <SafeHydrate><Calendar className="size-5 text-secondary" /></SafeHydrate>
                    <div>
                      <p className="text-sm text-muted-foreground">Sessions per Month</p>
                      <p className="font-semibold text-foreground">{programme.sessionsPerMonth}</p>
                    </div>
                  </div>

                  {programme.id === 1 && <div className="space-y-3 pb-4 border-b border-border">
                      <div>
                        <p className="text-xs text-muted-foreground italic">
                          Exam Techniques e-book included
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground italic">
                          Access to WhatsApp group for further support
                        </p>
                      </div>
                    </div>}

                  {programme.id === 3 && <div className="pb-4 border-b border-border">
                      <p className="text-xs text-muted-foreground italic">
                        Exam Techniques e-book included
                      </p>
                    </div>}

                  {programme.id === 4 && <div className="pb-4 border-b border-border">
                      <p className="text-xs text-muted-foreground italic">
                        How to Revise e-book is also included in this package
                      </p>
                    </div>}

                  <div className="pt-4 border-t border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Pay-per-Session</span>
                      <span className="font-bold text-primary">£{programme.payPerSession}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Monthly</span>
                      <span className="font-bold text-primary">£{programme.monthly}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Annual</span>
                      <span className="font-bold text-foreground">£{programme.annual}</span>
                    </div>
                  </div>

                  {programme.id === 4 && <div className="mt-4 p-3 bg-accent-light rounded-md">
                      <p className="text-xs text-foreground-accent font-medium">
                        Limited Availability
                      </p>
                    </div>}
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Our group tutoring programmes offer exceptional value and results. One-to-one tutoring is available only on a limited basis. Contact us to learn more about which programme is right for you or to discuss a bespoke programme just for your child.
          </p>
          <Link to="/ContactPage">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-foreground-secondary">
              Contact Us for More Information
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}